// Holding tanks in the packaging-route table (engine v7.152.0; transfers v7.153.0).
//
// A tank is named in the ordinary `kitchen` / `packingLineN` cells: a name the
// tanks table declares IS the vessel. A line cell holding one means the segment
// fills it; the kitchen cell means it drains it. A row with a tank in `kitchen`
// and, in a line cell, a KITCHEN of the same route (`TANK02 -> PKN12`) is a
// transfer into that second kitchen: that line cell is not a packing line. Only the routes grid is meant
// to show that. Every other view that reads route rows — the factory layout,
// the product edit panels and the kitchen / line pickers — treats those cells
// as real kitchens and lines, so it would draw a tank twice (once as a kitchen,
// once as a line at 0 u/hr) and offer it as a carry-over kitchen, a switching
// line or a trial unit.
//
// Before v7.152.0 those views never saw a tank: a drain row's kitchen was blank
// and a fill row's lines were blank, with the tank in columns of its own. So
// they are handed rows with the tank cells blanked (`withoutTanks`), which is
// exactly the shape they were built against, and anything they save is put
// back (`restoreTankCells`) before it reaches the server.
import { useMemo } from 'react'
import { useScheduleProductionRequirements } from './Scheduler.data'
import { SchedulerPackagingRoutesRowType } from './Scheduler.types'

const SLOTS = [1, 2, 3, 4] as const

/** A cell as the engine reads it: blank, "null" and "n/a" are all absent. */
export const cellText = (v: unknown): string | null => {
  if (v == null) return null
  const s = String(v).trim()
  return s === '' || ['null', 'n/a', 'nan', 'none'].includes(s.toLowerCase())
    ? null
    : s
}

/** The declared tank ids. Shares its query with every other `tanks` read. */
export const useDeclaredTanks = (
  schedulerId: string,
  iteration?: string,
  enabled = true
): Set<string> => {
  const { data } = useScheduleProductionRequirements(
    schedulerId,
    'tanks',
    iteration,
    undefined,
    enabled
  )
  return useMemo(
    () =>
      new Set(
        ((data?.data ?? []) as { tankId?: unknown }[])
          .map((t) => cellText(t?.tankId))
          .filter((t): t is string => t != null)
      ),
    [data]
  )
}

const isTank = (v: unknown, tanks: Set<string>) => {
  const t = cellText(v)
  return t != null && tanks.has(t)
}

type AnyRow = Record<string, unknown>

/** routeId -> the real (non-tank) kitchens its rows run on. */
export const routeKitchens = (
  rows: readonly unknown[],
  tanks: Set<string>
): Map<string, Set<string>> => {
  const out = new Map<string, Set<string>>()
  for (const r of rows as AnyRow[]) {
    const id = cellText(r?.routeId)
    const k = cellText(r?.kitchen)
    if (id == null || k == null || tanks.has(k)) continue
    if (!out.has(id)) out.set(id, new Set())
    out.get(id)!.add(k)
  }
  return out
}

/** The line slots of a transfer row (`TANK02 -> PKN12`) that hold its second
 *  kitchen: the row's kitchen is a tank, and the slot names a kitchen of the
 *  same route. Empty for every other row. Mirrors `parse_packing_routes`. */
export const transferSlots = (
  row: unknown,
  tanks: Set<string>,
  kitchensByRoute: Map<string, Set<string>>
): number[] => {
  const r = row as AnyRow
  if (!isTank(r?.kitchen, tanks)) return []
  const id = cellText(r?.routeId)
  const kitchens = id == null ? undefined : kitchensByRoute.get(id)
  if (!kitchens) return []
  return SLOTS.filter((i) => {
    const l = cellText(r?.[`packingLine${i}`])
    return l != null && kitchens.has(l)
  })
}

/** Route rows with every tank cell blanked: a tank in `kitchen` becomes no
 *  kitchen, and a tank in a line slot empties that slot (line, rate and PR).
 *  A transfer row's second kitchen is emptied too: it is not a packing line.
 *  Rows naming no tank come back as the same objects. */
export const withoutTanks = <T extends SchedulerPackagingRoutesRowType>(
  rows: T[],
  tanks: Set<string>
): T[] => {
  if (tanks.size === 0) return rows
  const byRoute = routeKitchens(rows, tanks)
  return rows.map((r) => {
    const kitchenTank = isTank(r?.kitchen, tanks)
    const lineTanks: number[] = [
      ...SLOTS.filter((i) => isTank(r?.[`packingLine${i}` as keyof T], tanks)),
      ...transferSlots(r, tanks, byRoute),
    ]
    if (!kitchenTank && lineTanks.length === 0) return r
    const out = { ...r } as Record<string, unknown>
    if (kitchenTank) out.kitchen = null
    for (const i of lineTanks) {
      out[`packingLine${i}`] = null
      out[`lineRunRate${i}`] = null
      out[`pr${i}`] = null
    }
    return out as T
  })
}

/** Put the tank cells back on rows a tank-blind view is saving.
 *
 *  The views save WHOLE rows built from what they were shown, so a blanked
 *  cell would be written back blank and erase the tank — and since the
 *  table's merge key includes `kitchen`, a drain row saved with no kitchen
 *  would not even match its stored row, and would be added as a duplicate.
 *
 *  Restored from the stored row, per changed row:
 *  - `kitchen`, when it held a tank (a Tank -> Lines drain, or a transfer);
 *  - all four line slots, when any held a tank (a fill) or the row is a
 *    transfer (`TANK02 -> PKN12`). A fill runs at its kitchen's rate and the
 *    engine ignores lines beside a tank, and a transfer packs nothing, so a
 *    line edited onto either in one of these views would change nothing. */
export const restoreTankCells = (
  changed: Record<number, Record<string, unknown>>,
  stored: SchedulerPackagingRoutesRowType[],
  tanks: Set<string>
): Record<number, Record<string, unknown>> => {
  if (tanks.size === 0) return changed
  const byId = new Map(
    stored.map((r) => [(r as unknown as { id: number }).id, r])
  )
  const byRoute = routeKitchens(stored, tanks)
  const out: Record<number, Record<string, unknown>> = {}
  for (const [key, row] of Object.entries(changed)) {
    const id = Number(key)
    const orig = byId.get(id) as unknown as Record<string, unknown> | undefined
    const next = { ...row }
    if (orig) {
      if (isTank(orig.kitchen, tanks)) next.kitchen = orig.kitchen
      if (
        SLOTS.some((i) => isTank(orig[`packingLine${i}`], tanks)) ||
        transferSlots(orig, tanks, byRoute).length > 0
      ) {
        for (const i of SLOTS) {
          for (const f of [`packingLine${i}`, `lineRunRate${i}`, `pr${i}`]) {
            next[f] = orig[f]
          }
        }
      }
    }
    out[id] = next
  }
  return out
}
